import React, { Fragment, useEffect, useState } from 'react';
import { Collapse } from 'react-collapse';
import { uniqBy } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'Components/frameworks/button/Button';
import { Icon } from 'Components/frameworks/icon/Icon';
import { Layer } from 'Components/frameworks/layer/Layer';
import { Modal } from 'Components/frameworks/modal/Modal';
import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { scrollToElement } from 'Components/utils/scroll/scrollToElement';
import { FacetCategory } from 'Components/widgets/criteria/criteria-facets/search-facets/FacetCategory';
import { FacetList } from 'Components/widgets/criteria/criteria-facets/search-facets/FacetList';
import {
  getActiveFacetTabTrackingData,
  getTypesOfStayFacetBadgeIconProps,
  getFacetLocalizedLabel,
  onClearFacets,
  trackFacetChange,
} from 'Components/widgets/criteria/criteria-facets/search-facets/SearchFacets.utils';
import type { ProductProps } from 'Components/widgets/product/Product.utils';
import { t } from 'Src/i18n';
import {
  clearAll,
  clearFacets,
  setOrderByPrice,
  setSearchFilterList,
  toggleFacet,
} from 'Store/faceting/facetingSearch.action';
import {
  filteredProductsSelector,
  productsBeforeActiveCustomFacetsFilteringSelector,
} from 'Store/faceting/faceting.selector';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import {
  ACTIVITIES_FACET_ID,
  COUNTRIES_FACET_ID,
  CHILDCARE_FILTER_FACET_ID,
  CHILDCARE_FILTER_FACET_LABELS,
  CUSTOM_FACETS_IDS,
  DESTINATIONS_FACET_ID,
  FLIGHT_DURATION_FACET_ID,
  FLIGHT_DURATION_FACET_LABELS,
  SEASON_FACET_FIRST_OPTION,
  SEASON_FACET_ID,
  SEASON_FACET_LABELS,
  TYPES_OF_STAY_FACET_BADGE_ICON_PROPS,
  TYPES_OF_STAY_FACET_ID,
  TYPES_OF_STAY_FACET_LABELS,
} from 'Utils/faceting/constants';
import { filterProductsByFlightDuration } from 'Components/widgets/facet-search-results/search-results/SearchResults.utils';
import { filterFacetsToHide, getInactiveFacetValues } from 'Utils/faceting/facetingUtils';
import type { ThumbnailProduct } from 'Utils/resort-thumbnail-query/resortThumbnailQuery';
import { capitalizeFromUppercase } from 'Utils/string/stringUtils';
import { HeaderLayerContent } from './HeaderLayerContent';
export type Props = {
  activeCustomFacets: any;
  activeFacets: any;
  clearFacetList: typeof clearFacets;
  clearAll: typeof clearAll;
  facets: Array<SearchFacetCategory>;
  facetTemplateConfig: Array<FacetTemplateConfig>;
  orderByPrice: string;
  setOrderByPrice: typeof setOrderByPrice;
  toggleFacet: typeof toggleFacet;
};
export const NUMBER_OF_TOP_FACETS = 7;

const getTopFacets = (facets: Array<SearchFacetCategory>, id: string, activeFacets) => {
  switch (id) {
    case ACTIVITIES_FACET_ID:
      const selectedActivities = Object.keys(activeFacets[ACTIVITIES_FACET_ID] || {});
      return facets
        .find((facet) => facet.id === id)
        ?.list.reduce((acc, cur) => {
          return [...acc, ...(cur.list || [])];
        }, [])
        .sort((facetA, facetB) => Number(facetB.count) - Number(facetA.count))
        .sort(
          (facetA, facetB) =>
            Number(selectedActivities.includes(facetB.value)) -
            Number(selectedActivities.includes(facetA.value)),
        );

    case CHILDCARE_FILTER_FACET_ID:
      return facets
        .find((facet) => facet.id === id)
        ?.list.map((facet) => {
          const label = getFacetLocalizedLabel({
            facetValue: facet.value,
            facetLabels: CHILDCARE_FILTER_FACET_LABELS,
          });
          return { ...facet, label };
        });

    case DESTINATIONS_FACET_ID:
      const selectedCountries = Object.keys(activeFacets[COUNTRIES_FACET_ID] || {});
      return uniqBy(
        facets
          .find((facet) => facet.id === COUNTRIES_FACET_ID)
          ?.list.reduce((acc, cur) => {
            return [...acc, ...(cur.list || [])];
          }, [])
          .sort(
            (facetA, facetB) =>
              Number(selectedCountries.includes(facetB.value)) -
              Number(selectedCountries.includes(facetA.value)),
          ),
        (facet) => facet.value,
      );

    case FLIGHT_DURATION_FACET_ID:
      return facets
        .find((facet) => facet.id === id)
        ?.list.map((facet) => {
          const label = getFacetLocalizedLabel({
            facetValue: facet.value,
            facetLabels: FLIGHT_DURATION_FACET_LABELS,
          });
          return { ...facet, label };
        });

    case SEASON_FACET_ID:
      return facets
        .find((facet) => facet.id === id)
        ?.list.map((facet) => {
          const label = getFacetLocalizedLabel({
            facetValue: facet.value,
            facetLabels: SEASON_FACET_LABELS,
          });
          return { ...facet, label };
        })
        .sort((facetA) => (facetA.value === SEASON_FACET_FIRST_OPTION ? -1 : 1));

    case TYPES_OF_STAY_FACET_ID:
      return facets
        .find((facet) => facet.id === id)
        ?.list.map((facet) => {
          const label = getFacetLocalizedLabel({
            facetValue: facet.label,
            facetLabels: TYPES_OF_STAY_FACET_LABELS,
          });
          return {
            ...facet,
            badgeIconProps: getTypesOfStayFacetBadgeIconProps({
              facetBadgeIcons: TYPES_OF_STAY_FACET_BADGE_ICON_PROPS,
              facetLabel: label,
              facetValue: facet.label,
            }),
            label,
          };
        });

    default:
      return facets.find((facet) => facet.id === id)?.list;
  }
};

export const SearchFacets = (props: Props) => {
  const { activeCustomFacets, activeFacets, facets, facetTemplateConfig = [] } = props;
  const [isLayerOpened, setIsLayerOpened] = useState(false);
  const dispatch = useDispatch();
  const { isDesktop } = useResponsive();
  const products: Array<ThumbnailProduct> = useSelector(
    (state) => filteredProductsSelector(state) || [],
  );
  const productsBeforeActiveCustomFacetsFiltering: Array<ProductProps> = useSelector(
    productsBeforeActiveCustomFacetsFilteringSelector,
  );
  const track = useTracking();
  const productsWithoutCustomFacetFiltering =
    productsBeforeActiveCustomFacetsFiltering !== null
      ? productsBeforeActiveCustomFacetsFiltering
      : products;
  const inactiveFlightDurationFacets = getInactiveFacetValues(
    facets,
    activeCustomFacets,
    FLIGHT_DURATION_FACET_ID,
  );
  const flightDurationFacetsToHide = inactiveFlightDurationFacets.filter((facet) => {
    const remainingProducts = filterProductsByFlightDuration({
      facet,
      productList: productsWithoutCustomFacetFiltering,
    });
    return remainingProducts.length === 0;
  });
  const filteredFacets = filterFacetsToHide(facets, {
    [FLIGHT_DURATION_FACET_ID]: flightDurationFacetsToHide,
  });

  const setSearchFilter = () => {
    const searchFilterList = {};
    facetTemplateConfig.forEach((templateConfig) => {
      const { dataLabel, id, titleLabel } = templateConfig;
      const topFacets = getTopFacets(filteredFacets, id, activeFacets) ?? [];

      if (topFacets.length > 0) {
        const topFacetsId = id === DESTINATIONS_FACET_ID ? COUNTRIES_FACET_ID : id;
        const activeCount = CUSTOM_FACETS_IDS.includes(topFacetsId)
          ? Object.keys(activeCustomFacets?.[topFacetsId] ?? {}).length
          : Object.keys(activeFacets?.[topFacetsId] ?? {}).length;
        searchFilterList[topFacetsId] = {
          activeCount,
          dataLabel,
          title: titleLabel,
          topFacets,
        };
      }
    });
    dispatch(setSearchFilterList(searchFilterList));
  };

  useEffect(() => {
    setSearchFilter();
  }, [
    activeCustomFacets,
    activeFacets,
    JSON.stringify(facetTemplateConfig),
    JSON.stringify(filteredFacets),
  ]);

  const changeFacet = (name: string, checked: boolean, value: string | boolean) => {
    const labelGA = trackFacetChange(name, checked, value);

    if (labelGA) {
      track({
        actionGA: 'smartCriterias',
        labelGA,
      });
    }

    props.toggleFacet({
      categoryId: name,
      isSelected: checked,
      value,
    });
    scrollToElement('.Line-ProductsFilters', 250, '.Navigation-bar');
  };

  const activeCustomFacetCount = activeCustomFacets
    ? Object.keys(activeCustomFacets)
        .map((key) => Object.keys(activeCustomFacets[key]).length)
        .reduce((a, b) => a + b, 0)
    : 0;
  const activeFacetCount = activeFacets
    ? Object.keys(activeFacets)
        .map((key) => Object.keys(activeFacets[key]).length)
        .reduce((a, b) => a + b, 0)
    : 0;
  const totalActiveCount = activeCustomFacetCount + activeFacetCount;
  const headerContent = (
    <HeaderLayerContent activeCount={totalActiveCount} title={t('faceting.filters.filtersTitle')} />
  );
  const resetButton = (
    <button
      className="ms-auto self-baseline font-sans text-14 underline hocus:text-black-active"
      onClick={props.clearAll}
      type="button"
    >
      {t('common.comparator.resetCriteria')}
    </button>
  );
  const header = (
    <div className="flex items-center gap-5 border-b-1 border-lightGrey px-20 py-15 text-20 font-bold">
      {headerContent}
      {resetButton}
    </div>
  );

  const getFacetBlocks = () => {
    return facetTemplateConfig
      .map((templateConfig) => {
        const { id, titleLabel = '' } = templateConfig;
        const title = capitalizeFromUppercase(t(titleLabel));

        if (id === FLIGHT_DURATION_FACET_ID) {
          const flightDurationFacet = filteredFacets.find((facet) => facet.id === id);
          const flightDurationFacetOptions = flightDurationFacet?.list ?? [];

          if (flightDurationFacetOptions.length === 0) {
            return null;
          }
        }

        return (
          <FacetBlock
            key={id}
            title={title}
            templateConfig={templateConfig}
            id={id}
            onChange={changeFacet}
            facets={filteredFacets}
            activeCustomFacets={activeCustomFacets}
            activeFacets={activeFacets}
            headerContent={headerContent}
            clearFacetList={props.clearFacetList}
          />
        );
      })
      .filter(Boolean);
  };

  return isDesktop ? (
    <div className="border-e-1 border-lightGrey sm:w-1/3 lg:min-w-1/4">
      {header}
      {getFacetBlocks()}
    </div>
  ) : (
    <Fragment>
      <Button
        className="order-first w-full min-w-auto"
        color={COLORS.DARK_GREY}
        colorBg={COLORS.WHITE}
        colorBorder={COLORS.GREY}
        iconRight={{
          name: ICONS.FILTERS,
        }}
        onClick={() => {
          setIsLayerOpened(true);
          track({
            actionGA: 'smartCriterias',
            labelGA: 'CTA_mobile',
          });
        }}
      >
        {t('faceting.filters.filtersButton')}
      </Button>
      {isLayerOpened && (
        <Layer
          ariaLabel={t('faceting.filters.filtersTitle')}
          bgColor={COLORS.WHITE}
          buttonLeft={{
            iconName: ICONS.ARROW_TAIL_LEFT,
            label: t('common.backNavigation'),
            onClick: () => {
              setIsLayerOpened(false);
            },
            className: 'flex-1',
          }}
          buttonRight={{
            label: t('common.comparator.resetCriteria'),
            onClick: () => props.clearAll(),
            className: 'text-14 underline',
          }}
          closeLabel={t('common.close')}
          header={<div className="flex justify-center text-18 font-bold">{headerContent}</div>}
          hasAnimateEnter
          hideCloseButton
        >
          <div className="flex h-full flex-col">
            <div className="scroll-y">{getFacetBlocks()}</div>
            <div className="mt-auto border-t p-10">
              <Button
                className="w-full"
                data-testid="apply-filters"
                onClick={() => {
                  setIsLayerOpened(false);
                }}
                size="medium"
              >
                {t('common.apply')}
              </Button>
            </div>
          </div>
        </Layer>
      )}
    </Fragment>
  );
};

const FacetBlock = ({
  title,
  templateConfig,
  facets,
  onChange,
  activeCustomFacets,
  activeFacets,
  id,
  headerContent,
  clearFacetList,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isModalOpened, setIsModalOpened] = useState(false);
  const { isDesktop } = useResponsive();
  const track = useTracking();
  const currentCustomFacetActiveFilterList = Object.keys(activeCustomFacets[id] ?? {});
  const currentFacetActiveFilterList = Object.keys(activeFacets[id] ?? {});
  useEffect(() => {
    const currentCustomFacetHasActiveFilters = currentCustomFacetActiveFilterList.length > 0;
    const currentFacetHasActiveFilters = currentFacetActiveFilterList.length > 0;
    setIsExpanded(currentCustomFacetHasActiveFilters || currentFacetHasActiveFilters);
  }, [currentCustomFacetActiveFilterList.length, currentFacetActiveFilterList.length, id]);
  const facetCategoryComponent = (
    <div className="flex flex-col gap-y-20 p-20">
      <FacetCategory
        templateConfig={templateConfig}
        facetsFromSearch={facets}
        onChange={onChange}
        activeCustomFacets={activeCustomFacets}
        activeFacets={activeFacets}
        key={id}
        name={id}
      />
    </div>
  );
  const ModalTagName = isDesktop ? Modal : Layer;
  const modalProps = isDesktop
    ? {
        closeLabel: t('common.close'),
        onClose: (e) => {
          e.preventDefault();
          setIsModalOpened(false);
        },
        header: (
          <div className="border-b border-lightGrey p-20 text-20 font-bold leading-1">{title}</div>
        ),
        hasCloseButton: true,
        size: 'medium',
        children: facetCategoryComponent,
      }
    : {
        ariaLabel: t('faceting.filters.filtersTitle'),
        buttonLeft: {
          iconName: ICONS.ARROW_TAIL_LEFT,
          label: t('common.backNavigation'),
          onClick: () => {
            setIsModalOpened(false);
          },
          className: 'flex-1',
        },
        hideCloseButton: true,
        buttonRight: {
          label: t('common.comparator.resetCriteria'),
          onClick: () => onClearFacets(clearFacetList, id, templateConfig),
          className: 'text-14 underline',
        },
        closeLabel: t('common.close'),
        header: <div className="flex justify-center text-18">{headerContent}</div>,
        hasAnimateEnter: true,
        bgColor: COLORS.WHITE,
        children: (
          <div className="flex h-full flex-col">
            <div className="scroll-y">{facetCategoryComponent}</div>
            <div className="mt-auto border-t p-10">
              <Button
                className="w-full"
                data-testid="apply-filters"
                onClick={() => {
                  setIsModalOpened(false);
                }}
                size="medium"
              >
                {t('common.apply')}
              </Button>
            </div>
          </div>
        ),
      };
  const topFacets = getTopFacets(facets, id, activeFacets) ?? [];
  const topFacetsId = id === DESTINATIONS_FACET_ID ? COUNTRIES_FACET_ID : id;
  const { capitalizeLabels = false, dataLabel = '' } = templateConfig;
  return (
    <div className="border-b-1 border-lightGrey px-20 py-30">
      <button
        className="flex w-full items-center justify-between gap-10 text-20 font-bold"
        data-testid={`${dataLabel}Filter`}
        onClick={() => {
          if (!isExpanded) {
            track(
              getActiveFacetTabTrackingData({
                facetId: dataLabel,
                isActive: isExpanded,
              }),
            );
          }

          setIsExpanded(!isExpanded);
        }}
        type="button"
      >
        <span>{title}</span>
        <Icon
          className="shrink-0"
          rotation={isExpanded ? 0 : 180}
          name={ICONS.ARROW_TOP}
          width="0.75.rem"
        />
      </button>
      <Collapse
        theme={{
          collapse: 'collapse-transition',
          content: 'flex flex-col gap-y-20',
        }}
        isOpened={isExpanded}
      >
        <FacetList
          activeCustomFacets={activeCustomFacets[topFacetsId]}
          activeFacets={activeFacets[topFacetsId]}
          capitalizeLabels={capitalizeLabels}
          className="mt-20"
          facets={topFacets.slice(0, NUMBER_OF_TOP_FACETS)}
          key={topFacetsId}
          name={topFacetsId}
          onChange={onChange}
          isTopFacets
        />
        {topFacets.length > NUMBER_OF_TOP_FACETS && (
          <button
            type="button"
            onClick={() => {
              track({
                actionGA: 'smartCriterias',
                labelGA: `${dataLabel}_seeMore`,
              });
              setIsModalOpened(true);
            }}
            className="text-initial text-14 underline hocus:text-black-active"
          >
            {t('faceting.filters.viewMoreFilters')}
          </button>
        )}
      </Collapse>
      {isModalOpened && <ModalTagName {...modalProps} />}
    </div>
  );
};
